import React from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { UserProfile, Application } from '../../types';
import { MASTER_STAGES } from '../MasterPipeline';
import { usePrograms } from '../../hooks/usePrograms';
import { getPipelineStages, stageLabel as getStageLabel } from '../../lib/programPipeline';
import { auth } from '../../lib/firebase';
import {
  X, RefreshCw, CheckCircle, XCircle, Send, Eye, UserPlus, Shield,
  KeyRound, AlertCircle, ChevronLeft, ChevronRight, ArrowRight, Building,
  Clock, Copy, ExternalLink, IdCard, Monitor, Search, Filter,
  Users, UserCheck, ShieldCheck, Briefcase, UserX, MoreVertical
} from 'lucide-react';
import InternationalPhoneInput from '../shared/InternationalPhoneInput';
import StructuredAddressForm, { flattenAddress, parseAddress } from '../shared/StructuredAddressForm';
import type { AddressValues } from '../shared/StructuredAddressForm';
import Modal from '../shared/Modal';
import { sanitizeNamePart } from '../../lib/nameUtils';


interface UsersTabProps {
  allUsers: UserProfile[];
  usersLoading: boolean;
  selectedUser: UserProfile | null;
  isBlockingUser: boolean;
  applications: Application[];
  onRefresh: () => void;
  onSelectUser: (user: UserProfile | null) => void;
  onToggleBlock: (user: UserProfile) => void;
  onSendPasswordReset: (email: string) => void;
  onCreateUser: (email: string) => Promise<{ success: boolean; error?: string }>;
  onSendPromotionCode: (targetUid: string) => Promise<{ success: boolean; error?: string }>;
  onVerifyPromotionCode: (targetUid: string, code: string) => Promise<{ success: boolean; error?: string }>;
  onSendRoleChangeCode: (targetUid: string, newRole: string) => Promise<{ success: boolean; error?: string }>;
  onVerifyRoleChangeCode: (targetUid: string, code: string, newRole: string) => Promise<{ success: boolean; error?: string }>;
  onChangeRoleDirect: (targetUid: string, newRole: string) => Promise<{ success: boolean; error?: string }>;
  onPromoteToEmployer: (uid: string, employerId: string, email?: string) => Promise<{ success: boolean; error?: string }>;
  onFetchEmployersList: () => Promise<{ id: string; companyName: string }[]>;
  canEdit?: boolean;
}

function LinkedApplicantRecords({ selectedUser, applications }: { selectedUser: UserProfile; applications: Application[] }) {
  const { programs } = usePrograms();
  const userApps = applications.filter(a => a.userId === selectedUser.uid || (!!selectedUser.email && a.userEmail?.toLowerCase() === selectedUser.email.toLowerCase()));
  if (userApps.length === 0) return null;

  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 uppercase mb-3">Applicant Records ({userApps.length})</h4>
      <div className="space-y-2">
        {userApps.map(app => {
          const matchedProg = (programs || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
          const stages = getPipelineStages(matchedProg);
          const stageLabel = getStageLabel(stages, app.masterStage, app.status || 'Draft');
          return (
            <div key={app.applicationId} className="bg-white border border-slate-200 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-800 truncate">{app.personalDetails?.firstName || ''} {app.personalDetails?.lastName || ''}</p>
                <span className="text-caption font-bold px-2 py-0.5 rounded-lg bg-brand-active">{stageLabel}</span>
              </div>
              <p className="text-caption text-slate-500 truncate">ID: {app.applicationId}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UsersTab({
  allUsers, usersLoading, selectedUser, isBlockingUser, applications,
  onRefresh, onSelectUser, onToggleBlock, onSendPasswordReset,
  onCreateUser, onSendPromotionCode, onVerifyPromotionCode,
  onSendRoleChangeCode, onVerifyRoleChangeCode, onChangeRoleDirect,
  onPromoteToEmployer, onFetchEmployersList, canEdit = true,
}: UsersTabProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'active' | 'blocked'>('all');
  const [sortBy, setSortBy] = React.useState<'newest' | 'oldest' | 'email'>('newest');
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [newUserEmail, setNewUserEmail] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(25);

  // Toggle dropdown state for action menus
  const [activeMenuUid, setActiveMenuUid] = React.useState<string | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = React.useState<{
    isOpen: boolean;
    title: string;
    description: string;
    actionLabel: string;
    variant: 'danger' | 'warning' | 'primary';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    description: '',
    actionLabel: '',
    variant: 'primary',
    onConfirm: () => {},
  });

  React.useEffect(() => { setCurrentPage(1); }, [itemsPerPage, roleFilter, statusFilter, searchTerm, sortBy]);

  // Didit verification state
  const [diditLoading, setDiditLoading] = React.useState<'reminder' | 'session' | null>(null);
  const [diditMessage, setDiditMessage] = React.useState('');
  const [diditError, setDiditError] = React.useState('');
  const [diditSessionUrl, setDiditSessionUrl] = React.useState('');
  const [diditCopied, setDiditCopied] = React.useState(false);
  const [showReverificationModal, setShowReverificationModal] = React.useState(false);
  const [reverificationReason, setReverificationReason] = React.useState('');

  // Profile editing state (before reverification/session)
  const [editMode, setEditMode] = React.useState(false);
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [editPhone, setEditPhone] = React.useState('');
  const [editAddress, setEditAddress] = React.useState<AddressValues>(() => ({
    addressLine1: '', addressLine2: '', city: '', state: '', country: '', postalCode: '',
  }));
  const [profileSaving, setProfileSaving] = React.useState(false);

  // Sync edit fields when selectedUser changes
  React.useEffect(() => {
    if (selectedUser) {
      setEditFirstName(selectedUser.firstName || '');
      setEditLastName(selectedUser.lastName || '');
      setEditPhone((selectedUser as any).phoneNumber || '');
      setEditAddress(parseAddress((selectedUser as any).address, selectedUser as any));
      setEditMode(false);
    }
  }, [selectedUser]);

  const handleSaveProfile = async () => {
    if (!selectedUser) return;
    setProfileSaving(true);
    setDiditError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const flatAddr = flattenAddress(editAddress);
      const res = await fetch('/api/admin/users/update-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId: selectedUser.uid,
          firstName: editFirstName.trim(),
          lastName: editLastName.trim(),
          phoneNumber: editPhone.trim(),
          address: flatAddr,
          addressLine1: editAddress.addressLine1,
          addressLine2: editAddress.addressLine2,
          city: editAddress.city,
          state: editAddress.state,
          country: editAddress.country,
          postalCode: editAddress.postalCode,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiditError(data.error || 'Failed to update profile');
      } else {
        setDiditMessage('✅ Profile updated successfully.');
        setEditMode(false);
        onRefresh?.();
      }
    } catch {
      setDiditError('Network error updating profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (selectedUser) {
      setEditFirstName(selectedUser.firstName || '');
      setEditLastName(selectedUser.lastName || '');
      setEditPhone((selectedUser as any).phoneNumber || '');
      setEditAddress(parseAddress((selectedUser as any).address, selectedUser as any));
    }
    setDiditError('');
    setEditMode(false);
  };

  const handleSendVerificationReminder = async (user: UserProfile) => {
    setDiditLoading('reminder');
    setDiditMessage('');
    setDiditError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/didit/send-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: user.uid }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiditError(data.error || 'Failed to send reminder');
      } else {
        setDiditMessage('✅ Verification reminder sent successfully.');
      }
    } catch {
      setDiditError('Network error sending reminder');
    } finally {
      setDiditLoading(null);
    }
  };

  const handleCreateSession = async (user: UserProfile) => {
    setDiditLoading('session');
    setDiditMessage('');
    setDiditError('');
    setDiditSessionUrl('');
    setDiditCopied(false);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/didit/create-session-for-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: user.uid }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiditError(data.error || 'Failed to create session');
        return;
      }
      if (data.status === 'approved') {
        setDiditMessage('✅ User is already verified.');
        return;
      }
      if (data.sessionUrl) {
        setDiditSessionUrl(data.sessionUrl);
        setDiditMessage(data.isNew
          ? '✅ New session created. Link copied to clipboard!'
          : '✅ Existing active session retrieved. Link copied to clipboard!'
        );
        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(data.sessionUrl);
          setDiditCopied(true);
        } catch {
          // Clipboard API may fail in some contexts; link is still shown below
        }
      } else {
        // A successful response with no session link (and not already
        // verified) must not leave the admin staring at a silent panel.
        setDiditError('The verification service did not return a session link. Please try again.');
      }
    } catch {
      setDiditError('Network error creating session');
    } finally {
      setDiditLoading(null);
    }
  };

  const getDiditStatusBadge = (user: UserProfile) => {
    const status = (user as any).didit?.status || 'not_started';
    switch (status) {
      case 'approved':
        return { label: 'Verified', cls: 'bg-green-50 text-green-700 border border-green-200', icon: CheckCircle };
      case 'declined':
        return { label: 'Declined', cls: 'bg-red-50 text-red-700 border border-red-200', icon: XCircle };
      case 'pending':
        return { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border border-amber-200', icon: Clock };
      case 'in_review':
        return { label: 'In Review', cls: 'bg-brand-active border border-blue-200', icon: Clock };
      case 'expired':
        return { label: 'Expired', cls: 'bg-orange-50 text-orange-700 border border-orange-200', icon: AlertCircle };
      case 'reverification_required':
        return { label: 'Re-Verify Required', cls: 'bg-purple-50 text-purple-700 border border-purple-200', icon: AlertCircle };
      default:
        return { label: 'Not Started', cls: 'bg-slate-100 text-slate-500 border border-slate-200', icon: IdCard };
    }
  };

  // Promotion state
  const [showPromoteModal, setShowPromoteModal] = React.useState(false);
  // Role change state
  const [showRoleModal, setShowRoleModal] = React.useState(false);
  useBodyScrollLock(Boolean(selectedUser) || showCreateModal || showPromoteModal || showRoleModal);
  const [roleTarget, setRoleTarget] = React.useState<UserProfile | null>(null);
  const [selectedNewRole, setSelectedNewRole] = React.useState<string>('applicant');
  const [roleStep, setRoleStep] = React.useState<'select' | 'confirm-direct' | 'send' | 'verify' | 'link-company'>('select');
  const [roleVerifyCode, setRoleVerifyCode] = React.useState('');
  const [roleProcessing, setRoleProcessing] = React.useState(false);
  const [roleError, setRoleError] = React.useState('');
  const [roleMessage, setRoleMessage] = React.useState('');
  // Employer linking state (optional, after verification)
  const [employerList, setEmployerList] = React.useState<{ id: string; companyName: string }[]>([]);
  const [selectedEmployerId, setSelectedEmployerId] = React.useState('');
  const [employerSearch, setEmployerSearch] = React.useState('');
  const [promoteTarget, setPromoteTarget] = React.useState<UserProfile | null>(null);
  const [promoteStep, setPromoteStep] = React.useState<'send' | 'verify'>('send');
  const [verifyCode, setVerifyCode] = React.useState('');
  const [promoting, setPromoting] = React.useState(false);
  const [promoteError, setPromoteError] = React.useState('');
  const [promoteMessage, setPromoteMessage] = React.useState('');

  // Simulation state
  const [simulating, setSimulating] = React.useState(false);
  const [simulateError, setSimulateError] = React.useState('');

  const handleSimulateUser = async (user: UserProfile) => {
    setSimulating(true);
    setSimulateError('');
    try {
      const currentAdminUid = auth.currentUser?.uid;
      const currentAdminEmail = auth.currentUser?.email;
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/simulate-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targetUid: user.uid }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start simulation');
      
      // Construct isolated simulation URL for new browser tab/window
      const simUrl = `/?simToken=${encodeURIComponent(data.token)}&simUid=${encodeURIComponent(data.uid)}&simEmail=${encodeURIComponent(data.email || '')}&adminUid=${encodeURIComponent(currentAdminUid || '')}&adminEmail=${encodeURIComponent(currentAdminEmail || '')}`;
      
      // Open simulation in a completely separate browser window/tab.
      // The Admin session in the current tab remains 100% untouched and logged in.
      window.open(simUrl, '_blank', 'noopener,noreferrer');
      
      // Close the user detail modal in the admin window
      onSelectUser(null);
    } catch (err: any) {
      setSimulateError(err.message || 'Failed to start simulation');
    } finally {
      setSimulating(false);
    }
  };

  const roleCounts = React.useMemo(() => ({
    all: allUsers.length,
    applicant: allUsers.filter(u => (u.role || 'applicant') === 'applicant').length,
    employer: allUsers.filter(u => u.role === 'employer').length,
    referrer: allUsers.filter(u => u.role === 'referrer').length,
    admin: allUsers.filter(u => u.role === 'admin').length,
    blocked: allUsers.filter(u => u.blocked).length,
  }), [allUsers]);

  const filteredUsers = React.useMemo(() => {
    let result = allUsers.filter(u => {
      if (roleFilter !== 'all' && (u.role || 'applicant') !== roleFilter) return false;
      if (statusFilter === 'active' && u.blocked) return false;
      if (statusFilter === 'blocked' && !u.blocked) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const fullName = `${sanitizeNamePart(u.firstName)} ${sanitizeNamePart(u.lastName)}`.toLowerCase();
        return (
          (u.email || '').toLowerCase().includes(q) ||
          (u.uid || '').toLowerCase().includes(q) ||
          (u.displayName || '').toLowerCase().includes(q) ||
          fullName.includes(q)
        );
      }
      return true;
    });

    return result.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortBy === 'email') return (a.email || '').localeCompare(b.email || '');
      return 0;
    });
  }, [allUsers, roleFilter, statusFilter, searchTerm, sortBy]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const displayedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleCreateUser = async () => {
    if (!newUserEmail.trim() || !newUserEmail.includes('@')) return;
    setCreating(true);
    setCreateError('');
    const result = await onCreateUser(newUserEmail.trim());
    if (result.success) {
      setShowCreateModal(false);
      setNewUserEmail('');
    } else {
      setCreateError(result.error || 'Failed to create user');
    }
    setCreating(false);
  };

  const handleStartPromotion = (user: UserProfile) => {
    setPromoteTarget(user);
    setPromoteStep('send');
    setVerifyCode('');
    setPromoteError('');
    setPromoteMessage('');
    setShowPromoteModal(true);
  };

  const handleStartRoleChange = (user: UserProfile) => {
    setRoleTarget(user);
    setSelectedNewRole('applicant');
    setRoleStep('select');
    setRoleVerifyCode('');
    setRoleError('');
    setRoleMessage('');
    setEmployerList([]);
    setSelectedEmployerId('');
    setEmployerSearch('');
    setShowRoleModal(true);
  };

  const handleRoleSelectNext = () => {
    if (!roleTarget) return;
    const currentRole = roleTarget.role || 'applicant';
    if (selectedNewRole === currentRole) {
      setRoleError('User already has this role.');
      return;
    }
    setRoleError('');
    if (selectedNewRole === 'admin' || selectedNewRole === 'employer') {
      // Admin and employer require Master Admin verification code
      setRoleStep('send');
      handleSendRoleChangeCode();
    } else {
      // Applicant is direct
      setRoleStep('confirm-direct');
    }
  };

  const handleSendRoleChangeCode = async () => {
    if (!roleTarget) return;
    setRoleProcessing(true);
    setRoleError('');
    const result = await onSendRoleChangeCode(roleTarget.uid, selectedNewRole);
    if (result.success) {
      setRoleStep('verify');
      setRoleMessage('Ask the Master Admin for the verification code.');
    } else {
      setRoleError(result.error || 'Failed to send verification code');
    }
    setRoleProcessing(false);
  };

  const handleVerifyRoleChangeCode = async () => {
    if (!roleTarget || !roleVerifyCode.trim()) return;
    setRoleProcessing(true);
    setRoleError('');
    const result = await onVerifyRoleChangeCode(roleTarget.uid, roleVerifyCode.trim(), selectedNewRole);
    if (result.success) {
      if (selectedNewRole === 'employer') {
        // Role changed — offer optional company linking
        setRoleStep('link-company');
        setRoleVerifyCode('');
        setRoleProcessing(false);
        onFetchEmployersList().then(list => setEmployerList(list));
      } else {
        setShowRoleModal(false);
        setRoleTarget(null);
        setRoleVerifyCode('');
        setRoleStep('select');
      }
    } else {
      setRoleError(result.error || 'Invalid verification code');
      setRoleProcessing(false);
    }
  };

  const handleDirectRoleChange = async () => {
    if (!roleTarget) return;
    setRoleProcessing(true);
    setRoleError('');
    const result = await onChangeRoleDirect(roleTarget.uid, selectedNewRole);
    if (result.success) {
      setShowRoleModal(false);
      setRoleTarget(null);
      setRoleVerifyCode('');
      setRoleStep('select');
    } else {
      setRoleError(result.error || 'Failed to change role');
    }
    setRoleProcessing(false);
  };

  const handleLinkEmployer = async () => {
    if (!roleTarget || !selectedEmployerId) {
      // Skip — close modal
      setShowRoleModal(false);
      setRoleTarget(null);
      setRoleStep('select');
      return;
    }
    setRoleProcessing(true);
    setRoleError('');
    const result = await onPromoteToEmployer(roleTarget.uid, selectedEmployerId, roleTarget.email);
    if (result.success) {
      setShowRoleModal(false);
      setRoleTarget(null);
      setRoleStep('select');
    } else {
      setRoleError(result.error || 'Failed to link company');
      setRoleProcessing(false);
    }
  };

  const handleSendPromotionCode = async () => {
    if (!promoteTarget) return;
    setPromoting(true);
    setPromoteError('');
    const result = await onSendPromotionCode(promoteTarget.uid);
    if (result.success) {
      setPromoteStep('verify');
      setPromoteMessage('Ask the Master Admin for the verification code.');
    } else {
      setPromoteError(result.error || 'Failed to send verification code');
    }
    setPromoting(false);
  };

  const handleVerifyPromotionCode = async () => {
    if (!promoteTarget || !verifyCode.trim()) return;
    setPromoting(true);
    setPromoteError('');
    const result = await onVerifyPromotionCode(promoteTarget.uid, verifyCode.trim());
    if (result.success) {
      setShowPromoteModal(false);
      setPromoteTarget(null);
      setVerifyCode('');
      setPromoteStep('send');
    } else {
      setPromoteError(result.error || 'Invalid verification code');
    }
    setPromoting(false);
  };

  return (
    <div className="space-y-6 animate-fade-in text-slate-800 pb-12">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <div className="flex items-center space-x-2.5">
            <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-brand">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold font-display tracking-tight text-slate-900">User Management</h1>
              <p className="text-xs text-slate-500 mt-0.5">Manage system access, roles, accounts, and simulation</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2.5 shrink-0">
          {canEdit && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-xs font-bold bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl transition-all cursor-pointer flex items-center space-x-2 shadow-xs active:scale-[0.98]"
            >
              <UserPlus className="h-4 w-4" />
              <span>Create Account</span>
            </button>
          )}
          <button
            onClick={onRefresh}
            className="px-3.5 py-2 text-xs font-bold bg-slate-50 border border-slate-200/80 rounded-xl hover:bg-slate-100 text-slate-700 transition-all cursor-pointer flex items-center space-x-2"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${usersLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Role & Analytics Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { key: 'all', label: 'All Users', count: roleCounts.all, icon: Users, color: 'text-slate-700 bg-slate-100 border-slate-200' },
          { key: 'applicant', label: 'Applicants', count: roleCounts.applicant, icon: UserCheck, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { key: 'employer', label: 'Employers', count: roleCounts.employer, icon: Briefcase, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
          { key: 'referrer', label: 'Referrers', count: roleCounts.referrer, icon: ShieldCheck, color: 'text-purple-700 bg-purple-50 border-purple-200' },
          { key: 'admin', label: 'Administrators', count: roleCounts.admin, icon: Shield, color: 'text-rose-700 bg-rose-50 border-rose-200' },
        ].map(r => {
          const IconComponent = r.icon;
          const isSelected = roleFilter === r.key;
          return (
            <button
              key={r.key}
              onClick={() => setRoleFilter(r.key)}
              className={`relative flex flex-col justify-between p-4 rounded-2xl border transition-all cursor-pointer text-left ${
                isSelected
                  ? 'border-brand bg-white shadow-md ring-2 ring-blue-500/20'
                  : 'border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between w-full mb-2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{r.label}</span>
                <div className={`p-2 rounded-xl border ${r.color}`}>
                  <IconComponent className="h-4 w-4" />
                </div>
              </div>
              <div className="flex items-baseline justify-between w-full">
                <span className="text-2xl font-black text-slate-900">{r.count}</span>
                {isSelected && (
                  <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-blue-100 text-brand">Selected</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Search & Filter Controls */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="search"
            placeholder="Search by name, email address, or user ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-xs font-medium border border-slate-200 rounded-xl bg-slate-50/50 shadow-inner focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand focus:bg-white transition-all"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto shrink-0">
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${statusFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              All Status
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${statusFilter === 'active' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Active
            </button>
            <button
              onClick={() => setStatusFilter('blocked')}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${statusFilter === 'blocked' ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Blocked ({roleCounts.blocked})
            </button>
          </div>

          <div className="h-6 w-px bg-slate-200" />

          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as any)}
            className="text-xs font-semibold border border-slate-200 rounded-xl px-3 py-2 bg-slate-50/50 cursor-pointer outline-none focus:bg-white transition-all"
          >
            <option value="newest">Sort: Newest First</option>
            <option value="oldest">Sort: Oldest First</option>
            <option value="email">Sort: Email A-Z</option>
          </select>
        </div>
      </div>

      {/* Main Table Container */}
      {usersLoading ? (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-16 flex flex-col items-center justify-center space-y-3">
          <span className="animate-spin h-8 w-8 border-4 border-blue-200 border-t-brand rounded-full" />
          <p className="text-xs font-bold text-slate-400">Loading workspace users...</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-400 font-bold uppercase text-[10px] tracking-wider">
                  <th className="py-3.5 px-4">User Details</th>
                  <th className="py-3.5 px-4">UID</th>
                  <th className="py-3.5 px-4">Role</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Identity Verification</th>
                  <th className="py-3.5 px-4">Applications</th>
                  <th className="py-3.5 px-4">Created</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayedUsers.map(user => {
                  const userApps = applications.filter(a => a.userId === user.uid || (!!user.email && a.userEmail?.toLowerCase() === user.email.toLowerCase()));
                  const displayName = [sanitizeNamePart(user.firstName), sanitizeNamePart(user.lastName)].filter(Boolean).join(' ') || user.displayName || '';
                  const diditBadge = getDiditStatusBadge(user);
                  const DiditIcon = diditBadge.icon;
                  const isMenuOpen = activeMenuUid === user.uid;

                  return (
                    <tr
                      key={user.uid}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                      onClick={() => onSelectUser(user)}
                    >
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200/80 flex items-center justify-center text-xs font-black text-slate-700 shrink-0 shadow-xs">
                            {user.email?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900 truncate flex items-center gap-1.5">
                              <span>{user.email}</span>
                            </p>
                            {displayName ? (
                              <p className="text-[11px] text-slate-500 font-medium truncate">{displayName}</p>
                            ) : (
                              <p className="text-[11px] text-slate-500 font-mono truncate">No name recorded</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-[11px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">{user.uid?.slice(0, 10)}…</span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-lg capitalize inline-flex items-center gap-1 border ${
                          user.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          user.role === 'employer' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          user.role === 'referrer' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          user.role === 'sponsor' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                          'bg-slate-100 text-slate-700 border-slate-200'
                        }`}>
                          {user.role || 'applicant'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className={`inline-flex items-center space-x-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${
                          user.blocked ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${user.blocked ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                          <span>{user.blocked ? 'Blocked' : 'Active'}</span>
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className={`inline-flex items-center space-x-1 text-[10px] font-bold px-2 py-0.5 rounded-md ${diditBadge.cls}`}>
                          <DiditIcon className="h-3 w-3 shrink-0" />
                          <span>{diditBadge.label}</span>
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">{userApps.length}</span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-[11px] font-medium text-slate-500">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-1.5">
                          <button
                            onClick={() => onSelectUser(user)}
                            className="px-2.5 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold flex items-center space-x-1 transition-all cursor-pointer shadow-2xs"
                          >
                            <Eye className="h-3.5 w-3.5 text-slate-500" />
                            <span>Details</span>
                          </button>
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleSimulateUser(user)}
                              disabled={simulating}
                              className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 rounded-xl text-xs font-bold flex items-center space-x-1 transition-all cursor-pointer disabled:opacity-50"
                              title="Simulate this user in an isolated session tab"
                            >
                              <Monitor className="h-3.5 w-3.5" />
                              <span>Simulate</span>
                            </button>
                          )}
                          <div className="relative">
                            <button aria-label="User actions" title="User actions"
                              onClick={() => setActiveMenuUid(isMenuOpen ? null : user.uid)}
                              className="p-1.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-500 transition-all cursor-pointer"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            {isMenuOpen && (
                              <div className="absolute right-0 top-full mt-1.5 w-48 bg-white rounded-2xl shadow-xl border border-slate-200 py-1.5 z-30 animate-fade-in text-left">
                                {canEdit && (
                                  <>
                                    <button
                                      onClick={() => { setActiveMenuUid(null); handleStartRoleChange(user); }}
                                      className="w-full px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center space-x-2 transition-all cursor-pointer"
                                    >
                                      <Shield className="h-3.5 w-3.5 text-purple-600" />
                                      <span>Change Role</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setActiveMenuUid(null);
                                        setConfirmDialog({
                                          isOpen: true,
                                          title: user.blocked ? 'Unblock User Account?' : 'Block User Account?',
                                          description: user.blocked
                                            ? `Are you sure you want to unblock ${user.email}? They will regain access to the portal.`
                                            : `Are you sure you want to block ${user.email}? They will be immediately signed out and blocked from logging in.`,
                                          actionLabel: user.blocked ? 'Unblock Account' : 'Block Account',
                                          variant: user.blocked ? 'primary' : 'danger',
                                          onConfirm: () => onToggleBlock(user),
                                        });
                                      }}
                                      className={`w-full px-3.5 py-2 text-xs font-semibold flex items-center space-x-2 hover:bg-slate-50 transition-all cursor-pointer ${user.blocked ? 'text-emerald-600' : 'text-rose-600'}`}
                                    >
                                      {user.blocked ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                                      <span>{user.blocked ? 'Unblock User' : 'Block User'}</span>
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => {
                                    setActiveMenuUid(null);
                                    onSendPasswordReset(user.email!);
                                  }}
                                  className="w-full px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center space-x-2 transition-all cursor-pointer"
                                >
                                  <Send className="h-3.5 w-3.5 text-blue-600" />
                                  <span>Password Reset Email</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="py-16 text-center space-y-2">
              <UserX className="h-8 w-8 text-slate-300 mx-auto" />
              <p className="text-sm font-bold text-slate-700">No users found</p>
              <p className="text-xs text-slate-500">Try adjusting your search terms or filter criteria.</p>
            </div>
          )}

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/50">
              <div className="flex items-center space-x-3 text-xs text-slate-500">
                <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                <span className="text-slate-300">•</span>
                <div className="flex items-center space-x-1.5">
                  <span>Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="text-xs font-semibold border border-slate-200 rounded-lg px-2 py-1 bg-white cursor-pointer outline-none"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                  <span>per page</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className="px-3.5 py-1.5 text-xs font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer transition-all shadow-2xs"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Previous</span>
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  className="px-3.5 py-1.5 text-xs font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer transition-all shadow-2xs"
                >
                  <span>Next</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmDialog.isOpen && (
        <Modal onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))} ariaLabel="Confirmation Required">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full space-y-4">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-2xl ${confirmDialog.variant === 'danger' ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-brand'}`}>
                <AlertCircle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-slate-900">{confirmDialog.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{confirmDialog.description}</p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 border border-slate-200 text-xs font-bold text-slate-600 rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmDialog.onConfirm();
                  setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                }}
                className={`px-4 py-2 text-xs font-bold text-white rounded-xl transition-all cursor-pointer ${
                  confirmDialog.variant === 'danger' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand hover:bg-blue-700'
                }`}
              >
                {confirmDialog.actionLabel}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* User Detail Modal — centered popup similar to verification modal */}
      {selectedUser && (
        <Modal onClose={() => onSelectUser(null)} ariaLabel="User Details">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90dvh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center text-sm font-bold text-brand">
                  {selectedUser.email?.charAt(0).toUpperCase() || '?'}
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">{selectedUser.email}</h3>
                  <span className="text-caption font-mono text-slate-400">ID: {selectedUser.uid}</span>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => onSelectUser(null)} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center cursor-pointer transition-all"><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-6">
              {/* Account Information Card */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-3 border border-slate-200/80">
                <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                  <span className="text-xs font-extrabold text-slate-900 uppercase tracking-wider">Account Information</span>
                  {canEdit && (
                    editMode ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={handleCancelEdit}
                          disabled={profileSaving}
                          className="px-2.5 py-1 text-[10px] font-bold rounded-lg border border-slate-300 text-slate-500 hover:bg-white transition-all cursor-pointer disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={profileSaving}
                          className="px-2.5 py-1 text-[10px] font-bold rounded-lg btn-primary text-white transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1"
                        >
                          {profileSaving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <span>Save</span>}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-2.5 py-1 text-[10px] font-bold rounded-lg border border-slate-300 text-slate-600 hover:bg-white transition-all cursor-pointer"
                      >
                        Edit Account
                      </button>
                    )
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">First Name</span>
                    {editMode ? (
                      <input
                        value={editFirstName}
                        onChange={e => setEditFirstName(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1 font-semibold text-slate-800 outline-none focus:border-brand"
                      />
                    ) : (
                      <span className="font-bold text-slate-800">{selectedUser.firstName || '—'}</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Last Name</span>
                    {editMode ? (
                      <input
                        value={editLastName}
                        onChange={e => setEditLastName(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1 font-semibold text-slate-800 outline-none focus:border-brand"
                      />
                    ) : (
                      <span className="font-bold text-slate-800">{selectedUser.lastName || '—'}</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Email</span>
                    <span className="font-bold text-slate-800">{selectedUser.email}</span>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Phone Number</span>
                    {editMode ? (
                      <InternationalPhoneInput
                        value={editPhone}
                        countryCode={editAddress.country}
                        onChange={(e164) => setEditPhone(e164)}
                      />
                    ) : (
                      <span className="font-bold text-slate-800">{selectedUser.phoneNumber || '—'}</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Country</span>
                    <span className="font-bold text-slate-800">{editAddress.country || (selectedUser as any).country || '—'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Account Status</span>
                    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold ${selectedUser.blocked ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                      <span>{selectedUser.blocked ? 'Blocked' : 'Active'}</span>
                    </span>
                  </div>
                </div>

                {editMode && (
                  <div className="pt-2 border-t border-slate-200/60">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Structured Home Address</span>
                    <StructuredAddressForm values={editAddress} onChange={setEditAddress} />
                  </div>
                )}
              </div>

              {/* Employment Details Card */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-3 border border-slate-200/80">
                <span className="text-xs font-extrabold text-slate-900 uppercase tracking-wider block border-b border-slate-200/60 pb-2">Employment Details</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Position / Job Title</span>
                    <span className="font-bold text-slate-800">{(selectedUser as any).position || (selectedUser as any).jobTitle || (selectedUser.role === 'admin' ? 'Administrator' : 'Team Member')}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Employment / Start Date</span>
                    <span className="font-bold text-slate-800">{(selectedUser as any).startDate ? new Date((selectedUser as any).startDate).toLocaleDateString() : new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Department / Team</span>
                    <span className="font-bold text-slate-800">{(selectedUser as any).department || 'Operations'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Staff Status</span>
                    <span className="font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md text-[10px]">Active Staff</span>
                  </div>
                </div>
              </div>

              {/* Access & Activity Card */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-3 border border-slate-200/80">
                <span className="text-xs font-extrabold text-slate-900 uppercase tracking-wider block border-b border-slate-200/60 pb-2">Access & Activity</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Assigned Role</span>
                    <span className="text-xs font-black text-purple-700 capitalize">{selectedUser.workspaceRole || selectedUser.role || 'Member'}</span>
                  </div>
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Assigned Applicants</span>
                    <span className="text-xs font-black text-slate-800">{applications.filter(a => a.userId === selectedUser.uid || (!!selectedUser.email && a.userEmail?.toLowerCase() === selectedUser.email.toLowerCase())).length} Applicants</span>
                  </div>
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Last Login</span>
                    <span className="text-xs font-bold text-slate-700">{(selectedUser as any).lastLoginAt ? new Date((selectedUser as any).lastLoginAt).toLocaleString() : '—'}</span>
                  </div>
                </div>

                <div className="pt-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1.5">Permission Summary</span>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    {['applicants', 'events', 'tickets', 'finance', 'jobs', 'reports', 'settings', 'users'].map(modKey => {
                      const permMap = selectedUser.permissions || {};
                      const hasAccess = permMap[modKey] ? permMap[modKey] !== 'none' : (selectedUser.role === 'admin');
                      return (
                        <div key={modKey} className={`px-2 py-1.5 rounded-lg border text-[10px] font-extrabold flex items-center justify-between ${
                          hasAccess ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-white text-slate-400 border-slate-200'
                        }`}>
                          <span className="capitalize">{modKey}</span>
                          <span>{hasAccess ? '✓' : '✕'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 pt-2 border-t border-slate-200/60">
                  <div><span className="font-bold text-slate-400">Account Created:</span> {new Date(selectedUser.createdAt).toLocaleDateString()}</div>
                  <div><span className="font-bold text-slate-400">Invited Date:</span> {(selectedUser as any).invitedAt ? new Date((selectedUser as any).invitedAt).toLocaleDateString() : new Date(selectedUser.createdAt).toLocaleDateString()}</div>
                </div>
              </div>

              {/* Identity Verification Section */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-caption tracking-wider font-bold text-slate-400 flex items-center space-x-1.5">
                    <IdCard className="h-3.5 w-3.5" />
                    <span>Identity Verification</span>
                  </span>
                  {(() => {
                    const badge = getDiditStatusBadge(selectedUser);
                    const Icon = badge.icon;
                    return (
                      <span className={`inline-flex items-center space-x-1 text-xs font-bold px-2.5 py-1 rounded-lg ${badge.cls}`}>
                        <Icon className="h-3 w-3" />
                        <span>{badge.label}</span>
                      </span>
                    );
                  })()}
                </div>

                {/* Show extra info about the verification state */}
                {(selectedUser as any).didit?.verifiedAt && (
                  <p className="text-caption text-slate-500">
                    Verified: {new Date((selectedUser as any).didit.verifiedAt).toLocaleDateString()}
                  </p>
                )}
                {(selectedUser as any).didit?.reverificationReason && (
                  <p className="text-caption text-amber-700 bg-amber-50 rounded-lg p-2 border border-amber-200">
                    Re-verification reason: {(selectedUser as any).didit.reverificationReason}
                  </p>
                )}

                {/* Action buttons */}
                <div className="space-y-2 pt-1">
                  {(selectedUser as any).didit?.status !== 'approved' ? (
                    <>
                      <button
                        onClick={() => handleSendVerificationReminder(selectedUser)}
                        disabled={diditLoading === 'reminder'}
                        className="w-full py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {diditLoading === 'reminder' ? (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                        <span>Send Verification Reminder</span>
                      </button>

                      <button
                        onClick={() => handleCreateSession(selectedUser)}
                        disabled={diditLoading === 'session'}
                        className="w-full py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 btn-primary  text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {diditLoading === 'session' ? (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <ExternalLink className="h-3.5 w-3.5" />
                        )}
                        <span>{(selectedUser as any).didit?.status === 'pending' || (selectedUser as any).didit?.status === 'in_review' ? 'Copy Session Link' : 'Create Session & Copy Link'}</span>
                      </button>

                      {/* Show session link if available */}
                      {diditSessionUrl && (
                        <div className="bg-white border border-slate-200 rounded-lg p-2.5 flex items-center space-x-2">
                          <span className="text-caption text-slate-500 truncate flex-1 font-mono text-[11px]">{diditSessionUrl}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(diditSessionUrl).then(() => setDiditCopied(true));
                            }}
                            className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer"
                            title="Copy to clipboard"
                          >
                            <Copy className={`h-3.5 w-3.5 ${diditCopied ? 'text-emerald-600' : 'text-slate-400'}`} />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Verified — show "Request New Verification" button */
                    <button
                      onClick={() => setShowReverificationModal(true)}
                      className="w-full py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      <span>Request New Verification</span>
                    </button>
                  )}

                  {/* Feedback messages */}
                  {diditMessage && !diditSessionUrl && (
                    <p className="text-caption font-medium text-emerald-700 bg-emerald-50 rounded-lg p-2 border border-emerald-200">{diditMessage}</p>
                  )}
                  {diditError && (
                    <p className="text-caption font-medium text-red-700 bg-red-50 rounded-lg p-2 border border-red-200">{diditError}</p>
                  )}
                </div>
              </div>

              {/* Linked Applicant Records */}
              <LinkedApplicantRecords selectedUser={selectedUser} applications={applications} />

              {/* Actions */}
              <div className="space-y-2">
                {canEdit && selectedUser.role !== 'admin' && (
                  <button
                    onClick={() => { onSelectUser(null); handleStartPromotion(selectedUser); }}
                    className="w-full py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Shield className="h-4 w-4" /><span>Promote to Admin</span>
                  </button>
                )}
                {selectedUser.role !== 'admin' && (
                  <button
                    onClick={() => handleSimulateUser(selectedUser)}
                    disabled={simulating}
                    className="w-full py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {simulating ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Monitor className="h-4 w-4" />
                    )}
                    <span>{simulating ? 'Starting...' : 'Simulate User'}</span>
                  </button>
                )}
                {simulateError && (
                  <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{simulateError}</span>
                  </div>
                )}
                <button
                  onClick={() => onToggleBlock(selectedUser)}
                  disabled={isBlockingUser}
                  className={`w-full py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 ${selectedUser.blocked ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isBlockingUser ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>{selectedUser.blocked ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}<span>{selectedUser.blocked ? 'Unblock User' : 'Block User'}</span></>
                  )}
                </button>
                <button
                  onClick={() => onSendPasswordReset(selectedUser.email!)}
                  className="w-full py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-2 btn-primary  text-white"
                >
                  <Send className="h-4 w-4" /><span>Send Password Reset</span>
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <Modal onClose={() => { setShowCreateModal(false); setCreateError(''); }} ariaLabel="Create New User">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center space-x-2">
                <UserPlus className="h-5 w-5 text-emerald-600" />
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Create New User</h3>
                  <p className="text-caption text-slate-400 mt-0.5">Add a new user account</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { setShowCreateModal(false); setCreateError(''); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Email Address</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={e => setNewUserEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateUser(); }}
                  placeholder="user@example.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none  focus:bg-white"
                  autoFocus
                />
              </div>
              {createError && (
                <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{createError}</span>
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button onClick={() => { setShowCreateModal(false); setCreateError(''); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Cancel</button>
              <button onClick={handleCreateUser} disabled={creating || !newUserEmail.includes('@')} className="px-4 py-1.5 bg-brand-primary hover:bg-brand-primary-dark disabled:bg-slate-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5 shadow-xs">
                {creating ? <RefreshCw className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
                <span>{creating ? 'Creating...' : 'Create User'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Promote to Admin Modal */}
      {showPromoteModal && promoteTarget && (
        <Modal onClose={() => { setShowPromoteModal(false); setPromoteError(''); }} ariaLabel="Promote to Admin">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-purple-600" />
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Promote to Admin</h3>
                  <p className="text-caption text-slate-400 mt-0.5">Grant admin privileges to a user</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { setShowPromoteModal(false); setPromoteError(''); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {promoteStep === 'send' ? (
                <>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">Target User</span>
                      <span className="text-xs font-semibold text-slate-700">{promoteTarget.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">Current Role</span>
                      <span className="text-xs font-semibold capitalize text-brand">{promoteTarget.role || 'applicant'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">New Role</span>
                      <span className="text-xs font-semibold text-purple-700">admin</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    A verification code will be sent to the Master Admin email address. The promotion cannot proceed without the code.
                  </p>
                  {promoteError && (
                    <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{promoteError}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {promoteMessage && (
                    <div className="flex items-center space-x-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-xl">
                      <KeyRound className="h-4 w-4 shrink-0" />
                      <span>{promoteMessage}</span>
                    </div>
                  )}
                  <div>
                    <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Verification Code</label>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={e => { if (e.key === 'Enter') handleVerifyPromotionCode(); }}
                      placeholder="000000"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-center font-mono text-lg tracking-widest outline-none  focus:bg-white"
                      autoFocus
                      maxLength={6}
                    />
                  </div>
                  {promoteError && (
                    <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{promoteError}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button onClick={() => { setShowPromoteModal(false); setPromoteError(''); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Cancel</button>
              {promoteStep === 'send' ? (
                <button onClick={handleSendPromotionCode} disabled={promoting} className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5">
                  {promoting ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  <span>{promoting ? 'Sending...' : 'Send Verification Code'}</span>
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => { setPromoteStep('send'); setPromoteError(''); setPromoteMessage(''); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Back</button>
                  <button onClick={handleVerifyPromotionCode} disabled={promoting || verifyCode.length < 4} className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5">
                    {promoting ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
                    <span>{promoting ? 'Verifying...' : 'Confirm Promotion'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Re-Verification Modal */}
      {showReverificationModal && selectedUser && (
        <Modal onClose={() => { setShowReverificationModal(false); setReverificationReason(''); setDiditError(''); }} ariaLabel="Request New Verification">
          <div className="bg-white rounded-2xl shadow-2xl w-full p-6">
            <button aria-label="Close" title="Close" onClick={() => { setShowReverificationModal(false); setReverificationReason(''); setDiditError(''); }} className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer">
              <X className="h-4 w-4" />
            </button>
            <h3 className="text-sm font-bold text-slate-900 mb-1">Request New Verification</h3>
            <p className="text-xs text-slate-500 mb-4">
              This will email the user their own direct verification link to complete re-verification. If they already have an active session, that session is reused — no new session is created.
            </p>
            {diditError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-bold px-4 py-3 rounded-xl mb-4" role="alert">
                {diditError}
              </div>
            )}
            <div className="space-y-1 mb-4">
              <label className="text-xs font-semibold text-slate-600">Reason for re-verification <span className="text-brand">*</span></label>
              <textarea
                value={reverificationReason}
                onChange={e => setReverificationReason(e.target.value)}
                placeholder="Explain why re-verification is needed..."
                className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-blue-400 resize-none min-h-[80px]"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setShowReverificationModal(false); setReverificationReason(''); setDiditError(''); }}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (reverificationReason.trim().length < 10) {
                    setDiditError('Please provide a reason (at least 10 characters).');
                    return;
                  }
                  setDiditLoading('reminder');
                  setDiditError('');
                  try {
                    const token = await auth.currentUser?.getIdToken();
                    const res = await fetch('/api/admin/didit/request-reverification', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ userId: selectedUser.uid, reason: reverificationReason.trim() }),
                    });
                    const data = await res.json();
                    if (!res.ok) {
                      setDiditError(data.error || 'Failed to request re-verification');
                    } else {
                      setDiditMessage(`✅ ${data.message || 'Re-verification requested successfully.'}`);
                      setShowReverificationModal(false);
                      setReverificationReason('');
                      onRefresh?.();
                    }
                  } catch {
                    setDiditError('Network error. Please try again.');
                  } finally {
                    setDiditLoading(null);
                  }
                }}
                disabled={diditLoading === 'reminder'}
                className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-xl text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                {diditLoading === 'reminder' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                <span>{diditLoading === 'reminder' ? 'Requesting...' : 'Request New Verification'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showRoleModal && roleTarget && (
        <Modal onClose={() => { setShowRoleModal(false); setRoleError(''); }} ariaLabel="Change User Role">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-brand" />
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Change User Role</h3>
                  <p className="text-caption text-slate-400 mt-0.5">Modify portal access for this user</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { setShowRoleModal(false); setRoleError(''); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {roleStep === 'select' && (
                <>
                  {/* Current role summary */}
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">Target User</span>
                      <span className="text-xs font-semibold text-slate-700">{roleTarget.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">Current Role</span>
                      <span className={`text-xs font-bold capitalize px-2 py-0.5 rounded ${roleTarget.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-brand-active'}`}>{roleTarget.role || 'applicant'}</span>
                    </div>
                  </div>

                  {/* Role selector */}
                  <div>
                    <label className="text-caption uppercase font-bold text-slate-500 block mb-2">New Role</label>
                    <div className="space-y-2">
                      {['applicant', 'employer', 'sponsor', 'admin'].map(r => {
                        const isCurrent = (roleTarget.role || 'applicant') === r;
                        const requiresVerify = r === 'admin' || r === 'employer';
                        return (
                          <button
                            key={r}
                            onClick={() => setSelectedNewRole(r)}
                            disabled={isCurrent}
                            className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${selectedNewRole === r ? 'border-brand bg-blue-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className={`text-sm font-bold capitalize ${r === 'admin' ? 'text-purple-700' : r === 'employer' ? 'text-amber-700' : r === 'sponsor' ? 'text-cyan-700' : 'text-slate-700'}`}>{r === 'sponsor' ? 'Sponsor / Partner' : r}</span>
                                {requiresVerify && <span className="ml-2 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Requires verification</span>}
                              </div>
                              {isCurrent && <span className="text-[10px] font-bold text-slate-400">Current</span>}
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {r === 'admin' ? 'Full system access. Can manage users, applications, and settings.' :
                               r === 'employer' ? 'Can post jobs, review applicants, and manage their company.' :
                               r === 'sponsor' ? 'Can manage assigned applicants, export SEVIS XML, and record DS-2019 data.' :
                               'Can submit applications and browse jobs.'}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {(selectedNewRole === 'admin' || selectedNewRole === 'employer') && (
                    <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <p>Changes to <strong>Admin</strong> or <strong>Employer</strong> roles require a verification code sent to the Master Admin email.</p>
                    </div>
                  )}

                  {roleError && (
                    <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{roleError}</span>
                    </div>
                  )}
                </>
              )}

              {roleStep === 'confirm-direct' && (
                <>
                  <div className="flex items-center justify-center py-4">
                    <div className="h-16 w-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
                      <AlertCircle className="h-8 w-8 text-amber-600" />
                    </div>
                  </div>
                  <div className="text-center space-y-2">
                    <h4 className="text-sm font-extrabold text-slate-900">Demote to {selectedNewRole === 'applicant' ? 'Applicant' : selectedNewRole}?</h4>
                    <p className="text-xs text-slate-500">
                      This will change <strong>{roleTarget.email}</strong> from <strong className="capitalize">{roleTarget.role || 'applicant'}</strong> to <strong className="capitalize">{selectedNewRole}</strong>.
                      They will be signed out immediately and must sign back in.
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <p className="text-xs text-amber-800 font-semibold">
                      Note: If they are an admin or employer, custom claims will be cleared. They will lose all elevated access.
                    </p>
                  </div>
                  {roleError && (
                    <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{roleError}</span>
                    </div>
                  )}
                </>
              )}

              {(roleStep === 'send' || roleStep === 'verify') && (
                <>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">Target User</span>
                      <span className="text-xs font-semibold text-slate-700">{roleTarget.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">Current Role</span>
                      <span className="text-xs font-semibold capitalize text-brand">{roleTarget.role || 'applicant'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-caption font-bold text-slate-400">New Role</span>
                      <span className={`text-xs font-semibold capitalize ${selectedNewRole === 'admin' ? 'text-purple-700' : 'text-amber-700'}`}>{selectedNewRole}</span>
                    </div>
                  </div>
                  {roleStep === 'verify' && (
                    <>
                      {roleMessage && (
                        <div className="flex items-center space-x-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-xl">
                          <KeyRound className="h-4 w-4 shrink-0" />
                          <span>{roleMessage}</span>
                        </div>
                      )}
                      <div>
                        <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Verification Code</label>
                        <input
                          type="text"
                          value={roleVerifyCode}
                          onChange={e => setRoleVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          onKeyDown={e => { if (e.key === 'Enter') handleVerifyRoleChangeCode(); }}
                          placeholder="000000"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-center font-mono text-lg tracking-widest outline-none  focus:bg-white"
                          autoFocus
                          maxLength={6}
                        />
                      </div>
                    </>
                  )}
                  {roleError && (
                    <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{roleError}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Link-company step (optional, after verification) */}
            {roleStep === 'link-company' && (
              <div className="px-5 py-4 border-t border-blue-100 bg-blue-50">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <p className="text-sm font-extrabold text-slate-900">Role changed to Employer successfully!</p>
                </div>
                <div className="mb-3">
                  <label className="text-caption uppercase font-bold text-slate-500 block mb-1.5">Link to a Company (optional)</label>
                  <input
                    type="search"
                    placeholder="Search companies..."
                    value={employerSearch}
                    onChange={e => setEmployerSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white mb-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                  />
                  <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100 bg-white">
                    {employerList
                      .filter(e => !employerSearch || e.companyName.toLowerCase().includes(employerSearch.toLowerCase()))
                      .map(emp => (
                        <button
                          key={emp.id}
                          onClick={() => setSelectedEmployerId(emp.id)}
                          className={`w-full text-left px-3 py-2 text-xs font-semibold transition-all cursor-pointer bg-brand-active ${selectedEmployerId === emp.id ? 'bg-brand-active' : 'text-slate-700'}`}
                        >
                          <Building className="h-3.5 w-3.5 inline mr-1.5" />
                          {emp.companyName}
                        </button>
                      ))}
                    {employerList.length === 0 && (
                      <p className="px-3 py-3 text-xs text-slate-500 text-center">No companies found.</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  {selectedEmployerId && (
                    <button onClick={handleLinkEmployer} disabled={roleProcessing} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5">
                      {roleProcessing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Building className="h-3.5 w-3.5" />}
                      <span>{roleProcessing ? 'Linking...' : 'Link & Finish'}</span>
                    </button>
                  )}
                  <button onClick={() => { setShowRoleModal(false); setRoleTarget(null); setRoleStep('select'); }} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer">Skip → Done</button>
                </div>
                {roleError && (
                  <div className="mt-2 flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{roleError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              {roleStep === 'select' && (
                <>
                  <button onClick={() => { setShowRoleModal(false); setRoleError(''); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Cancel</button>
                  <button onClick={handleRoleSelectNext} className="px-4 py-1.5 btn-primary  text-white text-caption font-bold rounded-xl transition-all cursor-pointer flex items-center space-x-1.5">
                    <ArrowRight className="h-3 w-3" /><span>Next</span>
                  </button>
                </>
              )}
              {roleStep === 'confirm-direct' && (
                <>
                  <button onClick={() => { setRoleStep('select'); setRoleError(''); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Back</button>
                  <button onClick={handleDirectRoleChange} disabled={roleProcessing} className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5">
                    {roleProcessing ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                    <span>{roleProcessing ? 'Changing...' : 'Confirm & Change Role'}</span>
                  </button>
                </>
              )}
              {(roleStep === 'send' || roleStep === 'verify') && (
                <>
                  {roleStep === 'verify' && (
                    <button onClick={() => { setRoleStep('send'); setRoleError(''); setRoleMessage(''); setRoleProcessing(false); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Back</button>
                  )}
                  {roleStep === 'verify' && (
                    <button onClick={handleVerifyRoleChangeCode} disabled={roleProcessing || roleVerifyCode.length < 4} className="px-4 py-1.5 btn-primary  disabled:bg-blue-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5">
                      {roleProcessing ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
                      <span>{roleProcessing ? 'Verifying...' : 'Confirm Role Change'}</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </Modal>
      )}


    </div>
  );
}
